import { SortRepairRequestColumnsEnum } from "./sort-repair-request-columns.enum";
import { SortDirectionEnum } from "../spor-equipmnts/sort-direction.enum";
import { RepairRequestsListSortOrder } from "./repairRequestsListSortOrder";

/**
 * Преобразует формат сортировки из enum в формат TypeORM для EquipmentRepairRequestEntity
 * 
 * @param sortOrderModel - объект сортировки в формате { [SortRepairRequestColumnsEnum]: SortDirectionEnum }
 * @returns объект в формате TypeORM для передачи в GraphQL запрос
 * 
 * @example
 * // Входной формат:
 * { "sportEquipment.name": "ASC" }
 * 
 * // Выходной формат:
 * { sportEquipment: { name: "ASC" } }
 */
export function buildRepairRequestOrderBy(sortOrderModel: RepairRequestsListSortOrder): Record<string, any> | undefined {
  if (!sortOrderModel || Array.isArray(sortOrderModel) || typeof sortOrderModel !== 'object') {
    return undefined;
  }

  const result: Record<string, any> = {};
  let sportEquipmentOrder: Record<string, any> = {};

  // Проходим по всем ключам в sortOrderModel
  for (const [column, direction] of Object.entries(sortOrderModel)) {
    if (!direction) continue; // Пропускаем null/undefined значения

    const directionUpper = direction.toUpperCase() as SortDirectionEnum;

    // Маппинг enum значений на формат TypeORM
    switch (column) {
      // Простые поля (прямо в EquipmentRepairRequestEntity)
      case SortRepairRequestColumnsEnum.CREATED_AT:
        result.equipmentRepairRequest = { 
          ...(result.equipmentRepairRequest || {}),
          createdAt: directionUpper 
        };
        break;

      // Поля через связь sportEquipment
      case SortRepairRequestColumnsEnum.NAME:
        sportEquipmentOrder.name = directionUpper;
        break;
      
      case SortRepairRequestColumnsEnum.INVENTORY_NUMBER:
        sportEquipmentOrder.inventoryNumber = directionUpper;
        break;
      
      case SortRepairRequestColumnsEnum.TECHNICAL_CONDITION:
        sportEquipmentOrder.technicalCondition = directionUpper;
        break;
      
      case SortRepairRequestColumnsEnum.COMMISSIONING_DATE:
        sportEquipmentOrder.commissioningDate = directionUpper;
        break;

      // Поля через вложенные связи sportEquipment.equipmentType
      case SortRepairRequestColumnsEnum.EQUIPMENT_TYPE:
        // Примечание: используем equipmentTypeObject для сортировки по имени типа оборудования
        result.equipmentTypeObject = { name: directionUpper };
        break;

      // Поля через вложенные связи sportEquipment.sportZone
      case SortRepairRequestColumnsEnum.SPORT_ZONE:
        sportEquipmentOrder.sportZone = { name: directionUpper };
        break;
      
      // Поля через вложенные связи sportEquipment.sportSubZone
      case SortRepairRequestColumnsEnum.SPORT_SUBZONE:
        sportEquipmentOrder.sportSubZone = { name: directionUpper };
        break;
      case SortRepairRequestColumnsEnum.SPORT_KIND:
        result.sportKindObject = { name: directionUpper };
        break;
      case SortRepairRequestColumnsEnum.SPORT_OBJECT:
        result.sportObject = { name: directionUpper };
        break;
      case SortRepairRequestColumnsEnum.STATE_HISTORY_STATUS:
        result.stateHistory = { status: directionUpper };
        break;
      
      default:
        // Если поле не найдено в маппинге, используем как есть (для обратной совместимости)
        // Если это путь через точку, пытаемся разобрать его
        if (column.includes('.')) {
          const parts = column.split('.');
          if (parts.length === 2 && parts[0] === 'sportEquipment') {
            // Простая вложенность: "sportEquipment.name"
            sportEquipmentOrder[parts[1]] = directionUpper;
          } else if (parts.length === 3 && parts[0] === 'sportEquipment') {
            // Двойная вложенность: "sportEquipment.sportZone.name"
            const [, relationName, fieldName] = parts;
            sportEquipmentOrder[relationName] = { 
              ...(sportEquipmentOrder[relationName] || {}),
              [fieldName]: directionUpper 
            };
          }
        } else {
          result.equipmentRepairRequest = { 
            ...(result.equipmentRepairRequest || {}),
            [column]: directionUpper 
          };
        }
        break;
    }
  }

  // Если есть поля для сортировки через sportEquipment, добавляем их в результат
  if (Object.keys(sportEquipmentOrder).length > 0) {
    result.sportEquipment = sportEquipmentOrder;
  }

  // Возвращаем undefined если объект пустой
  return Object.keys(result).length > 0 ? result : undefined;
}

